"use strict"

const MINUTES_PER_DAY = 24 * 60
const MS_PER_DAY = 24 * 60 * 60 * 1000

function dateOrdinal(value) {
  const match = String(value || "")
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isFinite(utc) ? Math.floor(utc / MS_PER_DAY) : null
}

function clockMinutes(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null
  if (hours === 24 && minutes !== 0) return null
  return hours * 60 + minutes
}

function dateTimeMinutes(value, rowDate) {
  if (!value) return null
  const clock = clockMinutes(value.time)
  const rowOrdinal = dateOrdinal(rowDate)
  const valueOrdinal = dateOrdinal(value.date || rowDate)
  if (clock === null || rowOrdinal === null || valueOrdinal === null) return null
  return (valueOrdinal - rowOrdinal) * MINUTES_PER_DAY + clock
}

function addWindow(windows, start, end) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return
  const normalizedStart = Math.min(start, end)
  const normalizedEnd = Math.max(start, end)
  if (normalizedEnd <= normalizedStart) return
  if (!windows.some((window) => window.start === normalizedStart && window.end === normalizedEnd)) {
    windows.push({ start: normalizedStart, end: normalizedEnd })
  }
}

function issueCodes(row) {
  return new Set((row?.issues || []).map((issue) => issue.code))
}

function adjacentPlannedBoundary(row, dayRows, direction) {
  const currentStart = dateTimeMinutes(row?.planned?.start, row?.date)
  const currentEnd = dateTimeMinutes(row?.planned?.end, row?.date)
  const candidates = (dayRows || []).filter((candidate) => candidate !== row && candidate?.planned)

  if (direction === "next") {
    const starts = candidates
      .map((candidate) => dateTimeMinutes(candidate.planned.start, row.date))
      .filter((value) => Number.isFinite(value) && value >= currentEnd)
    return starts.length ? Math.min(...starts) : null
  }

  const ends = candidates
    .map((candidate) => dateTimeMinutes(candidate.planned.end, row.date))
    .filter((value) => Number.isFinite(value) && value <= currentStart)
  return ends.length ? Math.max(...ends) : null
}

function deviationWindows(row, dayRows = [], threshold = 10) {
  const windows = []
  const plannedStart = dateTimeMinutes(row?.planned?.start, row?.date)
  const plannedEnd = dateTimeMinutes(row?.planned?.end, row?.date)
  const actualStart = dateTimeMinutes(row?.actual?.start, row?.date)
  const actualEnd = dateTimeMinutes(row?.actual?.end, row?.date)
  const codes = issueCodes(row)
  const normalizedThreshold = Math.max(0, Number.isFinite(Number(threshold)) ? Number(threshold) : 10)
  const marked = (value) => Number.isFinite(value) && value !== 0 && Math.abs(value) >= normalizedThreshold

  if (row?.state === "explained" || row?.state === "missing" || codes.has("missing_attendance")) {
    addWindow(windows, plannedStart, plannedEnd)
  } else if (row?.kind === "unplanned" || codes.has("attendance_without_shift")) {
    addWindow(windows, actualStart, Number.isFinite(actualEnd) ? actualEnd : MINUTES_PER_DAY)
  }

  if (marked(row?.deltas?.start)) addWindow(windows, plannedStart, actualStart)
  if (marked(row?.deltas?.end)) addWindow(windows, plannedEnd, actualEnd)

  if (codes.has("missing_planned_checkout")) {
    const nextStart = adjacentPlannedBoundary(row, dayRows, "next")
    addWindow(windows, plannedEnd, Number.isFinite(nextStart) ? nextStart : actualEnd)
  }
  if (codes.has("missing_planned_checkin")) {
    const previousEnd = adjacentPlannedBoundary(row, dayRows, "previous")
    addWindow(windows, Number.isFinite(previousEnd) ? previousEnd : actualStart, plannedStart)
  }
  if (codes.has("missing_checkout")) addWindow(windows, Number.isFinite(plannedEnd) ? plannedEnd : actualStart, MINUTES_PER_DAY)

  if ((row?.kind === "invalid" || codes.has("duplicate_checkin") || codes.has("checkout_without_checkin")) && Number.isFinite(actualStart)) {
    addWindow(windows, actualStart, actualStart + 1)
  }

  if (windows.length === 0 && codes.size > 0) {
    if (Number.isFinite(plannedStart) && Number.isFinite(plannedEnd)) addWindow(windows, plannedStart, plannedEnd)
    else if (Number.isFinite(actualStart)) addWindow(windows, actualStart, Number.isFinite(actualEnd) ? actualEnd : actualStart + 1)
  }

  return windows
}

function timedContextInterval(context) {
  const start = clockMinutes(context?.start)
  if (start === null) return null
  const parsedEnd = clockMinutes(context?.end)
  const end = parsedEnd !== null && parsedEnd > start ? parsedEnd : start + 1
  return { start, end }
}

function contextOverlapsWindow(context, windows) {
  const interval = timedContextInterval(context)
  if (!interval) return false
  return windows.some((window) => interval.start < window.end && interval.end > window.start)
}

function shiftNoteMatchesRow(context, row) {
  if (context?.kind !== "shift_note") return false
  return (row?.planned?.shiftIds || []).some((shiftId) => context.id === `shift-note:${shiftId}`)
}

function contextsForRow(row, contexts = [], dayRows = [], threshold = 10) {
  const windows = deviationWindows(row, dayRows, threshold)
  if (windows.length === 0 && row?.state !== "explained") return []

  return contexts.filter((context) => {
    if (contextOverlapsWindow(context, windows)) return true
    if (shiftNoteMatchesRow(context, row)) return true
    return row?.state === "explained" && ["sick", "holiday", "freewish"].includes(context?.kind)
  })
}

module.exports = {
  clockMinutes,
  contextOverlapsWindow,
  contextsForRow,
  deviationWindows,
}
